import { ref, Ref } from 'vue';

import axios from 'axios';
import { i18n } from '@/i18n/i18n';

import { toastController } from '@ionic/vue';

import { getApiEndpoint } from '@/globals/api-endpoint';

const $t = i18n.global.t;

const checkAxios = axios.create({
  baseURL: getApiEndpoint(),
});

// We do not want to intercept errors (no need to log them)
checkAxios.interceptors.response.use(response => response, () => undefined);


class Connection {
  isConnected: Ref<boolean> = ref(true);

  lastCheck: Ref<undefined | Date> = ref(undefined);
  checkConnectionTimeout: undefined | NodeJS.Timeout = undefined;

  offlineAt: Ref<undefined | Date> = ref(undefined);

  count = 1;
  alwaysOffline = false;

  constructor() {
    if (this.alwaysOffline) {
      this.disable();
    }

    // Detect connectivity from the OS, not only from failed requests. The
    // WebView fires 'offline'/'online' (and reflects navigator.onLine) the
    // instant airplane mode toggles. Without this, cache-first reads never fail,
    // so the app could sit optimistically "online" while actually offline — and
    // the tracking sync would keep trying, holding its queue mutex and spinning
    // the stamp button. navigator.onLine === false is a reliable "offline"
    // signal (a true value only means an interface is up, so we never act on it
    // here — reconnection is confirmed by checkConnection()).
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('offline', () => this.goOffline(false));
      window.addEventListener('online', () => { this.checkConnection(); });
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.isConnected.value = false;
      this.offlineAt.value = new Date();
      this.setTimeout();
    }

    const fully = (window as any).fully;
    if (fully) {
      (window as any).fully__internetDisconnect = () => this.disable();
      (window as any).fully__internetConnect = () => this.enable();

      fully.bind('internetDisconnect','fully__internetDisconnect();')
      fully.bind('internetConnect','fully__internetConnect();')
    }
  }

  async checkConnection(autocheck=false): Promise<boolean> {
    this.lastCheck.value = new Date();
    try {
      if (!this.alwaysOffline) {
        const resp = await checkAxios.get('/api/v2/misc/hello/');
        if (resp && resp.status == 200) {
          this.enable();
          return true;
        }
      }
    } finally {
      this.count += 1;
      // Only keep the backoff poll alive while still offline. Once enable() has
      // flipped us back online (here on a 200, or via the 'online' event), the
      // poll has done its job — rescheduling would leave us probing
      // /misc/hello/ forever while online.
      if (autocheck && !this.isConnected.value) this.setTimeout();
    }
    return false;
  }

  getNextCheckInterval() {
    return Math.min(1.5 ** this.count, 60 * 5) * 1000;
  }

  setTimeout() {
    if (this.checkConnectionTimeout) clearTimeout(this.checkConnectionTimeout);

    this.checkConnectionTimeout = setTimeout(() => {
      this.checkConnection(true)
    }, this.getNextCheckInterval());
  }

  enable() {
    if (this.isConnected.value) return;

    this.isConnected.value = true;

    // Reset timer
    if (this.checkConnectionTimeout) {
      clearTimeout(this.checkConnectionTimeout);
      this.checkConnectionTimeout = undefined;
    }

    toastController.create({
      message: $t('message.notification_device_is_back_online'),
      duration: 5000,
      color: 'dark',
      position: 'top',
      cssClass: 'mt-4',
    }).then(toast => toast.present());

  }

  // Transition to the offline state. `redirect` keeps the legacy disable()
  // behaviour of bouncing to "/" (used by the request-failure path); the OS
  // 'offline' event marks us offline in place via goOffline(false), so the user
  // isn't yanked off whatever screen they're on the moment they go offline.
  goOffline(redirect: boolean) {
    if (!this.isConnected.value) return;

    this.isConnected.value = false;
    this.count = 3;
    this.offlineAt.value = new Date();

    this.setTimeout();

    if (redirect) (window as any).router.push("/");

    toastController.create({
      message: $t('message.notification_device_is_offline'),
      duration: 5000,
      color: 'dark',
      position: 'top',
      cssClass: 'mt-4',
    }).then(toast => toast.present());

  }

  disable() {
    this.goOffline(true);
  }
}

const connection = new Connection();

export { connection }
